import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { TodoItem, Priority } from '../../types/todo';
import { useTodoContext } from '../../contexts/TodoContext';
import { useToastContext } from '../../contexts/ToastContext';
import { theme } from '../../styles/theme';
import { getChildrenVisibilityStats, calculateSubtreeTimeEstimate, canHaveTimeEstimate, canAddChildToNode } from '../../utils/tree';
import { LAYOUT, MESSAGES } from '../../utils/constants';
import type { DropIndicator } from '../../hooks/useDragDrop';

import TodoNodeHeader from './TodoNodeHeader';
import TodoNodeDescription from './TodoNodeDescription';
import TodoNodeActions from './TodoNodeActions';
import TimeEstimatePicker from './TimeEstimatePicker';
import ContextMenu from '../ContextMenu';
import ProgressBar from '../ProgressBar';
import Modal from '../Modal';

/**
 * Global reference time for synchronized rainbow animations
 * All highlighted nodes sync their animation to this timeline
 */
const ANIMATION_CYCLE_DURATION = 4000; // 4 seconds in milliseconds
const getAnimationReferenceTime = (() => {
  const referenceTime = Date.now();
  return () => referenceTime;
})();

/**
 * Props for the TodoNode component - represents a single task in the tree visualization
 */
interface TodoNodeProps {
  /** Unique identifier for the todo item */
  id: string;
  /** The todo item data including name, status, priority, etc. */
  item: TodoItem;
  /** Whether this node has child nodes */
  hasChildren: boolean;
  /** Index of this node within its column (for positioning) */
  nodeIndex?: number;
  /** Index of the column this node belongs to */
  columnIndex?: number;
  /** Callback when user tries to complete a task that has incomplete children */
  onBlockedCompletion?: (incompleteChildren: Array<{ id: string; item: TodoItem }>) => void;
  /** Callback when parent tasks are marked incomplete due to adding children */
  onParentsMarkedIncomplete?: (parentIds: string[]) => void;
  /** Whether this node can be dragged (false for drag overlay) */
  isDraggable?: boolean;
  /** What dropping the currently dragged node onto this node would do (drives the drop-target highlight) */
  dropIndicator?: DropIndicator;
}

const TodoNode: React.FC<TodoNodeProps> = ({
  id,
  item,
  hasChildren,
  columnIndex = 0,
  nodeIndex = 0,
  onBlockedCompletion,
  onParentsMarkedIncomplete,
  isDraggable = true,
  dropIndicator = null,
}) => {
  const { tree, toggleDone, updateName, updateDescription, toggleArchived, addChild, setPriority, setTimeEstimate, toggleHighlighted, reportNodeHeight } = useTodoContext();
  const { showError } = useToastContext();
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);
  const [isPriorityDropdownOpen, setIsPriorityDropdownOpen] = useState(false);
  const [showAddChildModal, setShowAddChildModal] = useState(false);
  // Nodes that already have a description start expanded so it's visible by
  // default; empty nodes stay compact. Toggling later (via the header caret)
  // reveals/hides the full description and the priority detail in-flow;
  // ResizeObserver reports the new height so the layout reflows. User-initiated,
  // so it never loops.
  const [isExpanded, setIsExpanded] = useState(() => !!(item.description && item.description.trim()));
  const nodeContainerRef = useRef<HTMLDivElement | null>(null);
  const nodeContentRef = useRef<HTMLDivElement | null>(null);

  // Drag and drop functionality
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id,
    disabled: !isDraggable || item.archived,
  });

  // ResizeObserver for height tracking
  useEffect(() => {
    const element = nodeContainerRef.current;
    if (!element) return;

    // Check if ResizeObserver is available (not available in test environment)
    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const height = entry.contentRect.height;
        reportNodeHeight(id, height);
      }
    });

    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, [id, reportNodeHeight, item.description]); // Re-observe when description changes

  /**
   * Calculate synchronized animation delay for rainbow effect
   * All highlighted nodes sync to the same animation cycle
   */
  const calculateSynchronizedDelay = useCallback((): string => {
    const currentTime = Date.now();
    const referenceTime = getAnimationReferenceTime();
    const elapsed = currentTime - referenceTime;
    const positionInCycle = elapsed % ANIMATION_CYCLE_DURATION;
    // Use negative delay to sync to the global timeline
    const delay = -(positionInCycle);
    return `${delay}ms`;
  }, []);

  /**
   * Synchronize animation when highlight state changes
   * This forces the animation to restart with the correct delay
   */
  useEffect(() => {
    if (!nodeContentRef.current) return;

    const element = nodeContentRef.current;

    if (item.highlighted) {
      // Calculate the synchronized delay
      const delay = calculateSynchronizedDelay();

      // Force animation restart by removing and re-adding the animation
      // First, remove the animation
      element.style.animation = 'none';

      // Force a reflow to ensure the animation removal is applied
      void element.offsetHeight;

      // Re-apply the animation with the synchronized delay
      element.style.animation = `rainbow-cycle 4s linear infinite`;
      element.style.animationDelay = delay;
    } else {
      // Clear inline animation styles when unhighlighted
      // Let CSS take over (no animation for non-highlighted)
      element.style.animation = '';
      element.style.animationDelay = '';
    }
  }, [item.highlighted, calculateSynchronizedDelay]);

  const getNodeStyle = useCallback((): React.CSSProperties => {
    // Colors are CSS variable references so they re-resolve when the theme
    // attribute on <html> flips — no React re-render needed.
    const baseStyle: React.CSSProperties = {
      backgroundColor: 'var(--bg-elevated)',
      borderColor: 'var(--border-primary)',
      color: 'var(--text-primary)',
      transition: transition || `opacity ${theme.animations.durations.normal} ${theme.animations.easings.easeInOut}, box-shadow ${theme.animations.durations.normal} ${theme.animations.easings.easeInOut}`,
      transform: isDragging ? CSS.Transform.toString(transform) : undefined,
      opacity: isDragging ? 0.5 : 1,
      cursor: isDraggable && !item.archived ? 'grab' : 'pointer',
    };

    if (isDragging) {
      baseStyle.zIndex = 1000;
      baseStyle.boxShadow = 'var(--shadow-xl)';
    }

    if (item.blocked) {
      baseStyle.backgroundColor = 'var(--state-blocked-bg)';
      baseStyle.borderColor = 'var(--state-blocked)';
    }

    if (item.done) {
      baseStyle.backgroundColor = 'var(--state-done-bg)';
      baseStyle.borderColor = 'var(--state-done)';
    }

    if (item.archived) {
      baseStyle.opacity = 0.6;
      baseStyle.cursor = 'not-allowed';
    }

    return baseStyle;
  }, [isDragging, transform, transition, isDraggable, item]);

  const handleDoneToggle = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    toggleDone(id, onBlockedCompletion);
  }, [toggleDone, id, onBlockedCompletion]);

  const handleArchiveClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    toggleArchived(id);
  }, [toggleArchived, id]);

  const handleAddChildClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Check if parent has a time estimate before prompting for child name
    if (!canAddChildToNode(tree, id)) {
      showError('Cannot add child to a task with a time estimate. Remove the estimate first.');
      return;
    }
    setShowAddChildModal(true);
  }, [id, tree, showError]);

  const handleAddChildConfirm = useCallback((childName?: string) => {
    if (childName && childName.trim()) {
      addChild(id, childName.trim(), onParentsMarkedIncomplete, (message) => {
        showError(message);
      });
    }
    setShowAddChildModal(false);
  }, [addChild, id, onParentsMarkedIncomplete, showError]);

  const handlePriorityChange = useCallback((priority: Priority) => {
    setPriority(id, priority);
  }, [setPriority, id]);

  const handleTimeEstimateChange = useCallback((hours: number | undefined) => {
    setTimeEstimate(id, hours);
  }, [setTimeEstimate, id]);

  const handleDropdownOpenChange = useCallback((isOpen: boolean) => {
    setIsPriorityDropdownOpen(isOpen);
  }, []);

  const handleToggleExpand = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsExpanded(prev => !prev);
  }, []);

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY });
  }, []);

  const handleCloseContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  const handleNodeBodyClick = useCallback((e: React.MouseEvent) => {
    // Only toggle highlight if clicking on the body, not on interactive elements
    const target = e.target as HTMLElement;
    const isInteractiveElement =
      target.tagName === 'INPUT' ||
      target.tagName === 'BUTTON' ||
      target.tagName === 'TEXTAREA' ||
      target.tagName === 'A' ||
      target.closest('input') ||
      target.closest('button') ||
      target.closest('textarea') ||
      target.closest('a') ||
      target.classList.contains('priority-badge') ||
      target.closest('.priority-badge');

    if (!isInteractiveElement) {
      toggleHighlighted(id);
    }
  }, [toggleHighlighted, id]);

  const getChildrenIndicator = useCallback(() => {
    if (!hasChildren) return null;

    const stats = getChildrenVisibilityStats(tree, id);
    const { visible, total, hiddenDone, hiddenArchived } = stats;

    let title = `${total} total children`;
    let displayText = `[${total}]`;

    if (item.visibility === 'hide-all') {
      title = `${total} children (all hidden)`;
      displayText = `[0/${total}]`;
    } else if (visible < total) {
      const hiddenCount = total - visible;
      title = `${visible} visible, ${hiddenCount} hidden (${hiddenDone} done, ${hiddenArchived} archived)`;
      displayText = `[${visible}/${total}]`;
    }

    return (
      <span className="children-indicator" title={title}>
        {displayText}
      </span>
    );
  }, [tree, id, hasChildren, item.visibility]);

  // Calculate position for free-floating layout
  const calculatePosition = useCallback(() => {
    const columnSpacing = LAYOUT.COLUMN_SPACING;
    const columnWidth = LAYOUT.NODE_WIDTH + columnSpacing;
    const left = columnIndex * columnWidth + LAYOUT.TREE_PADDING;

    // Use optimized Y position if available, otherwise fall back to simple calculation
    const top = item.yPosition !== undefined ? item.yPosition : (nodeIndex * LAYOUT.NODE_BASE_HEIGHT);

    return { left: `${left}px`, top: `${top}px` };
  }, [columnIndex, item.yPosition, nodeIndex]);

  const position = calculatePosition();

  // Combined ref callback for both drag-and-drop and ResizeObserver
  const setNodeRefs = useCallback((node: HTMLDivElement | null) => {
    nodeContainerRef.current = node;
    setNodeRef(node);
  }, [setNodeRef]);

  // Controls for the header's right cluster: the time-estimate picker (always
  // visible in the compact row) and the hover-revealed add-child / archive
  // buttons. Keeping them inline in the header avoids overlap with the caret.
  const headerControls = (
    <>
      <TimeEstimatePicker
        id={id}
        timeEstimate={item.timeEstimate}
        canHaveEstimate={canHaveTimeEstimate(tree, id)}
        subtreeEstimate={calculateSubtreeTimeEstimate(tree, id)}
        onTimeEstimateChange={handleTimeEstimateChange}
        onDropdownOpenChange={handleDropdownOpenChange}
        showWhenEmpty={isExpanded}
      />
      {!item.archived && (
        <span className="node-header-actions">
          <button className="add-child-button" onClick={handleAddChildClick} title="Add child task">
            ➕
          </button>
          <button className="archive-button" onClick={handleArchiveClick} title={MESSAGES.ARCHIVE_TOOLTIP}>
            📦
          </button>
        </span>
      )}
    </>
  );

  return (
    <div
      ref={setNodeRefs}
      id={id}
      className={`todo-node-free-floating ${item.priority ? `priority-${item.priority}` : ''} ${isDragging ? 'dragging' : ''} ${item.highlighted ? 'highlighted' : ''} ${isPriorityDropdownOpen ? 'priority-dropdown-open' : ''} ${isExpanded ? 'expanded' : ''} ${dropIndicator ? `drop-target drop-target-${dropIndicator}` : ''}`}
      style={{...position, ...getNodeStyle()}}
      onContextMenu={handleContextMenu}
    >
      <div ref={nodeContentRef} className="node-content" onClick={handleNodeBodyClick}>
        {hasChildren && (
          <ProgressBar tree={tree} nodeId={id} className="node-progress-bar" />
        )}

        <TodoNodeHeader
          id={id}
          item={item}
          hasChildren={hasChildren}
          onToggleDone={handleDoneToggle}
          onUpdateName={(name) => updateName(id, name)}
          isDraggable={isDraggable && !item.archived}
          attributes={attributes}
          listeners={listeners}
          childrenIndicator={getChildrenIndicator()}
          controls={headerControls}
          isExpanded={isExpanded}
          onToggleExpand={handleToggleExpand}
        />

        <TodoNodeDescription
          description={item.description}
          onUpdateDescription={(desc) => updateDescription(id, desc)}
          isArchived={item.archived}
          isExpanded={isExpanded}
        />

        <TodoNodeActions
          id={id}
          priority={item.priority}
          onPriorityChange={handlePriorityChange}
          onDropdownOpenChange={handleDropdownOpenChange}
        />
      </div>

      {contextMenu && (
        <ContextMenu
          id={id}
          item={item}
          position={contextMenu}
          onClose={handleCloseContextMenu}
          onAddChild={onParentsMarkedIncomplete}
        />
      )}

      <Modal
        isOpen={showAddChildModal}
        onClose={() => setShowAddChildModal(false)}
        onConfirm={handleAddChildConfirm}
        title="Add Child Task"
        message="Enter the name for the new child task:"
        variant="prompt"
        placeholder="Task name..."
        confirmText="Add"
      />
    </div>
  );
};

export default TodoNode;